<form action="" method="post"></form>
<template>
  <Component
    :is="Component"
    :title="$t('components.task_details.confirm_complete.title')"
    :description="description"
    :cancelLabel="$t('components.task_details.confirm_complete.buttons.cancel')"
    :confirmLabel="
      $t('components.task_details.confirm_complete.buttons.confirm')
    "
    @click="$emit('click', primaryAction?.id)"
  >
    <Button
      v-if="primaryAction"
      :label="!maxTablet ? $t(primaryAction?.label) : undefined"
      :aria-label="$t(primaryAction?.label)"
      variant="primary"
      :size="maxTablet ? 'xl' : 'md'"
      :type="primaryAction?.type"
      :icon="maxTablet"
      :disabled="busy"
      @click="
        primaryAction?.id === 'complete'
          ? null
          : $emit('click', primaryAction?.id)
      "
    >
      <template v-if="primaryAction?.icon" #start>
        <Icon :name="primaryAction?.icon" size="xl" />
      </template>
    </Button>
  </Component>
</template>

<script setup>
import { computed } from "vue";
import { Button } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { PrimaryActionType, primaryActions } from "@/SharedComponents/Tasks";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { ConfirmCompleteModal } from "@/SharedComponents/Modals";
import { useI18n } from "vue-i18n";

const { maxTablet } = useBreakpoints();
const { t } = useI18n();

defineEmits(["click"]);

const props = defineProps({
  task: { type: Object, required: true },
  canComplete: { type: Boolean, default: false },
  busy: { type: Boolean, required: false },
});

const hasOpenChecklistItems = computed(() =>
  props.task.checklist?.items?.some((item) => !item.checked)
);

const description = computed(() => {
  // Show a description if there are unchecked checklist items on desktop and mobile
  if (hasOpenChecklistItems.value) {
    return t(
      "components.task_details.confirm_complete_unchecked_checklist_items.description"
    );
  }

  // Don't show description on desktop if there are no unchecked checklist items
  if (!maxTablet.value) return null;

  // Show the default description on mobile
  return t("components.task_details.confirm_complete.description");
});

const Component = computed(() => {
  if (primaryAction.value?.id === "complete" && description.value) {
    return ConfirmCompleteModal;
  }

  return "span";
});

const type = computed(() => {
  if (props.task?.deleted_at) {
    return PrimaryActionType.RESTORE;
  }

  if (props.task?.completed) {
    return PrimaryActionType.REOPEN;
  }

  if (!props.canComplete) {
    return null;
  }

  return PrimaryActionType.COMPLETE;
});

const primaryAction = computed(() => {
  if (!type.value) {
    return null;
  }

  return primaryActions[type.value];
});
</script>
